import { KernelEventBus } from "../bus/event-bus.js";
import { diffRows } from "./diff.js";
import { LiveQueryRegistry } from "./registry.js";
export class LiveQueryEngine {
    #bus;
    #registry;
    #subscriptions = new Map();
    #unsubscribeByEventType = new Map();
    #nextSubscriptionId = 1;
    constructor(bus, registry) {
        this.#bus = bus;
        this.#registry = registry;
    }
    query(name, params) {
        return this.#registry.get(name).run(params);
    }
    register(definition) {
        this.#registry.register(definition);
    }
    subscribe(name, params, listener) {
        const subscriptionId = `live-query-${this.#nextSubscriptionId}`;
        this.#nextSubscriptionId += 1;
        const definition = this.#registry.get(name);
        const subscription = {
            definition: definition,
            initialized: false,
            listener: listener,
            params,
            previousRows: [],
            queued: false,
            revision: 0,
            tail: Promise.resolve(),
        };
        this.#subscriptions.set(subscriptionId, subscription);
        this.#ensureEventSubscriptions(definition.invalidateOn);
        this.#schedule(subscriptionId, subscription);
        return subscriptionId;
    }
    unsubscribe(subscriptionId) {
        this.#subscriptions.delete(subscriptionId);
        if (this.#subscriptions.size !== 0)
            return;
        for (const unsubscribe of this.#unsubscribeByEventType.values())
            unsubscribe();
        this.#unsubscribeByEventType.clear();
    }
    async refreshAll() {
        await Promise.all([...this.#subscriptions].map(async ([subscriptionId, subscription]) => (await this.#forceRefresh(subscriptionId, subscription))));
    }
    #ensureEventSubscriptions(eventTypes) {
        for (const eventType of eventTypes) {
            if (this.#unsubscribeByEventType.has(eventType))
                continue;
            this.#unsubscribeByEventType.set(eventType, this.#bus.subscribe(eventType, (event) => this.#handleEvent(event)));
        }
    }
    #handleEvent(event) {
        for (const [subscriptionId, subscription] of this.#subscriptions) {
            if (subscription.definition.invalidateOn.includes(event.type))
                this.#schedule(subscriptionId, subscription);
        }
    }
    #schedule(subscriptionId, subscription) {
        if (subscription.queued)
            return;
        subscription.queued = true;
        void this.#enqueue(subscription, async () => {
            subscription.queued = false;
            await this.#rerun(subscriptionId, subscription);
        });
    }
    #forceRefresh(subscriptionId, subscription) {
        return this.#enqueue(subscription, async () => {
            await this.#rerun(subscriptionId, subscription);
        });
    }
    #enqueue(subscription, operation) {
        const run = subscription.tail.then(operation, operation);
        subscription.tail = run;
        void run.catch(() => undefined);
        return run;
    }
    async #rerun(subscriptionId, subscription) {
        if (this.#subscriptions.get(subscriptionId) !== subscription)
            return;
        const currentRows = await subscription.definition.run(subscription.params);
        if (this.#subscriptions.get(subscriptionId) !== subscription)
            return;
        const change = diffRows(subscription.previousRows, currentRows, subscription.definition.key);
        const hasChanges = !subscription.initialized || change.added.length > 0 || change.deleted.length > 0 || change.updated.length > 0;
        subscription.previousRows = currentRows;
        subscription.initialized = true;
        if (hasChanges) {
            subscription.revision += 1;
            subscription.listener({ ...change, revision: subscription.revision });
        }
    }
}
//# sourceMappingURL=engine.js.map